import { useState, useEffect, useRef, useId } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { DeathVerificationSettings } from "@/components/DeathVerificationSettings";
import { MessageToExecutorCard } from "@/components/MessageToExecutorCard";
import { SecureFileSettings } from "@/components/SecureFileSettings";
import { checkBreachedPassword } from "@/lib/hibpCheck";
import { validatePasswordComplexity, PASSWORD_COMPLEXITY_MESSAGE } from "@/lib/passwordStrength";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { CA_PROVINCES, COUNTRIES, countryHasProvinces } from "@shared/constants/provinces";
import { FlagIcon } from "@/components/FlagIcon";
import type { Client } from "@shared/schema";
import { HOME_BOARD } from "@shared/plan-home/home-board.generated";
import { AlertTriangle } from "lucide-react";

interface LinkedAdvisor {
  id: string;
  advisorId: string;
  advisorName: string;
  advisorEmail: string;
  companyName: string | null;
  linkedAt: string;
}

function AdvisorLinkSection({ clientId }: { clientId: string }) {
  const uid = useId();
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [advisorName, setAdvisorName] = useState<string | null>(null);
  const [codeValid, setCodeValid] = useState<boolean | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: linkedAdvisors = [], isLoading } = useQuery<LinkedAdvisor[]>({
    queryKey: [`/api/clients/${clientId}/linked-advisors`],
  });

  const linkMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/link-advisor`, { advisorCode: code });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to link advisor");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/linked-advisors`] });
      setCode("");
      setAdvisorName(null);
      setCodeValid(null);
      toast({ title: "Advisor linked", description: "Your advisor has been linked to your account." });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (advisorId: string) => {
      const res = await apiRequest("DELETE", `/api/clients/${clientId}/link-advisor/${advisorId}`, undefined);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to unlink advisor");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${clientId}/linked-advisors`] });
      toast({ title: "Advisor unlinked" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const validateCode = async (value: string) => {
    if (!value.trim()) { setAdvisorName(null); setCodeValid(null); return; }
    setValidating(true);
    try {
      const res = await apiRequest("GET", `/api/auth/validate-advisor-code?code=${encodeURIComponent(value.trim())}`);
      const data = await res.json();
      setCodeValid(!!data.valid);
      setAdvisorName(data.valid ? data.advisorName : null);
    } catch {
      setCodeValid(false);
      setAdvisorName(null);
    } finally {
      setValidating(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setCode(val);
    setAdvisorName(null);
    setCodeValid(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => validateCode(val), 600);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your Advisor</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : linkedAdvisors.length > 0 ? (
          <div className="space-y-3">
            {linkedAdvisors.map(link => (
              <div key={link.id} className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-muted/30">
                <div>
                  <p className="text-sm font-medium">{link.advisorName}</p>
                  {link.companyName && (
                    <p className="text-xs text-muted-foreground">{link.companyName}</p>
                  )}
                  <p className="text-xs text-muted-foreground">{link.advisorEmail}</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => unlinkMutation.mutate(link.advisorId)}
                  disabled={unlinkMutation.isPending}
                >
                  Unlink
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No advisor linked yet.</p>
        )}

        <div className="space-y-2 pt-2">
          <Label className="text-sm" htmlFor={`${uid}-advisor-code`}>Link an advisor by code</Label>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input id={`${uid}-advisor-code`}
                placeholder="e.g. SMITH-FP-3847"
                value={code}
                onChange={handleCodeChange}
                className={codeValid === false ? "border-destructive" : codeValid === true ? "border-primary" : ""}
              />
              {advisorName && (
                <p className="text-xs text-primary mt-1">Code valid — {advisorName}</p>
              )}
              {codeValid === false && code && !validating && (
                <p className="text-xs text-destructive mt-1">Invalid code</p>
              )}
            </div>
            <Button
              type="button"
              onClick={() => linkMutation.mutate()}
              disabled={!codeValid || linkMutation.isPending}
            >
              {linkMutation.isPending ? "Linking..." : "Link"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface MfaStatus {
  mfaEnabled: boolean;
  phoneVerified: boolean;
  phoneLastFour: string;
}

// Security → two-factor authentication (SMS MFA, s90). Self-contained like
// AdvisorLinkSection: owns its own status query + enable/confirm/disable flow.
function MfaSecuritySection({ defaultPhone }: { defaultPhone?: string | null }) {
  const uid = useId();
  const { toast } = useToast();
  const { data: status, isLoading } = useQuery<MfaStatus>({
    queryKey: ["/api/auth/mfa-status"],
  });

  const [setupOpen, setSetupOpen] = useState(false);
  const [phone, setPhone] = useState(defaultPhone ?? "");
  const [codeSent, setCodeSent] = useState(false);
  const [setupCode, setSetupCode] = useState("");
  const [setupError, setSetupError] = useState("");
  const [showDisable, setShowDisable] = useState(false);

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170). Same token-rotation hazard as the executor invite: the route
  // overwrites `pendingMfaCode` before texting, so a double-click sends two billed SMS and the code
  // in the FIRST one stops working. `disabled={isPending}` cannot stop clicks inside one tick.
  const enableFlight = useSingleFlight();

  const enableMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/enable-mfa", { phoneNumber: phone });
      if (!res.ok) throw new Error((await res.json()).error || "Failed to send code");
      return res.json();
    },
    onSuccess: (data) => {
      setCodeSent(true);
      toast({
        title: "Code sent",
        description: data.smsSent
          ? `A code was sent to the number ending in ${data.phoneLastFour}.`
          : "Code generated (SMS may not have delivered on a trial number — enter the code to continue).",
      });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/confirm-mfa-setup", { code: setupCode });
      if (!res.ok) {
        const b = await res.json();
        throw new Error(
          b.error === "invalid_code"
            ? `Incorrect code. ${b.attemptsRemaining} attempt${b.attemptsRemaining === 1 ? "" : "s"} remaining.`
            : (b.message || b.error || "Failed to verify code")
        );
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/mfa-status"] });
      setSetupOpen(false); setCodeSent(false); setSetupCode(""); setSetupError("");
      toast({ title: "Two-factor authentication is enabled" });
    },
    onError: (e: any) => setSetupError(e.message),
  });

  const disableMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/auth/disable-mfa");
      if (!res.ok) throw new Error("Failed to disable");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/auth/mfa-status"] });
      setShowDisable(false);
      toast({ title: "Two-factor authentication disabled" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const cancelSetup = () => {
    setSetupOpen(false); setCodeSent(false); setSetupCode(""); setSetupError("");
    setPhone(defaultPhone ?? "");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Two-factor authentication
          {status?.mfaEnabled && (
            <Badge variant="default" data-testid="badge-mfa-enabled">Enabled</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : status?.mfaEnabled ? (
          <>
            <p className="text-sm text-muted-foreground">
              A verification code is sent to your phone (•••{status.phoneLastFour}) each time you sign in.
            </p>
            <Button variant="outline" onClick={() => setShowDisable(true)} data-testid="button-disable-mfa">
              Disable two-factor authentication
            </Button>
          </>
        ) : !setupOpen ? (
          <>
            <p className="text-sm text-muted-foreground">
              Add an extra layer of security to your account. When enabled, you'll need to enter a code
              sent to your phone each time you sign in.
            </p>
            <Button onClick={() => setSetupOpen(true)} data-testid="button-enable-mfa">
              Enable two-factor authentication
            </Button>
          </>
        ) : (
          <div className="space-y-3 p-4 border rounded-lg">
            {!codeSent ? (
              <>
                <Label className="text-sm" htmlFor={`${uid}-mfa-phone`}>Phone number</Label>
                <Input id={`${uid}-mfa-phone`}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+12025550123"
                  data-testid="input-mfa-phone"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => enableFlight.runQuiet(() => enableMutation.mutateAsync())}
                    disabled={!phone || enableFlight.inFlight || enableMutation.isPending}
                    data-testid="button-send-mfa-code"
                  >
                    {enableMutation.isPending ? "Sending…" : "Send code"}
                  </Button>
                  <Button variant="outline" onClick={cancelSetup}>Cancel</Button>
                </div>
              </>
            ) : (
              <>
                <Label className="text-sm" htmlFor={`${uid}-mfa-code`}>Enter the 6-digit code</Label>
                <Input id={`${uid}-mfa-code`}
                  value={setupCode}
                  onChange={(e) => { setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setSetupError(""); }}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  className="tracking-[0.3em]"
                  data-testid="input-mfa-setup-code"
                />
                {setupError && <p className="text-xs text-destructive">{setupError}</p>}
                <div className="flex gap-2">
                  <Button
                    onClick={() => confirmMutation.mutate()}
                    disabled={setupCode.length !== 6 || confirmMutation.isPending}
                    data-testid="button-confirm-mfa"
                  >
                    {confirmMutation.isPending ? "Verifying…" : "Verify and enable"}
                  </Button>
                  <Button variant="outline" onClick={() => { setCodeSent(false); setSetupCode(""); setSetupError(""); }}>
                    Back
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>

      <AlertDialog open={showDisable} onOpenChange={setShowDisable}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable two-factor authentication?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll no longer be asked for a code when you sign in. Your account will be less protected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => disableMutation.mutate()} disabled={disableMutation.isPending}>
              Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

const INVESTMENT_TYPES = ["RRSP", "TFSA", "RESP", "RDSP", "Non-registered", "Crypto"];

// Jurisdiction editor (Stephanie #9) — country + province, the same
// jurisdiction_country / jurisdiction_province captured at signup. Saves on
// change via the generic PATCH /api/clients/:id (accepts insertClientSchema
// partials). Self-contained like MfaSecuritySection.
function JurisdictionSection({ clientData }: { clientData: Client }) {
  const { toast } = useToast();
  const country = clientData.jurisdictionCountry || "CA";
  const province = clientData.jurisdictionProvince ?? "";

  const saveJurisdiction = useMutation({
    mutationFn: async (payload: { jurisdictionCountry: string; jurisdictionProvince: string | null }) => {
      const res = await apiRequest("PATCH", `/api/clients/${clientData.id}`, payload);
      if (!res.ok) throw new Error((await res.json()).message || "Failed to update");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Jurisdiction updated" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const hasProvinces = countryHasProvinces(country);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FlagIcon country={country} />
          Jurisdiction
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Your country and province determine which forms, checklists, and terminology apply to your plan.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Country</Label>
            <Select
              value={country}
              onValueChange={(v) =>
                // Clear province when switching to a country without a region list.
                saveJurisdiction.mutate({ jurisdictionCountry: v, jurisdictionProvince: countryHasProvinces(v) ? (province || null) : null })
              }
              disabled={saveJurisdiction.isPending}
            >
              <SelectTrigger data-testid="settings-select-country">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`settings-country-${c.code}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {hasProvinces && (
            <div className="space-y-1.5">
              <Label>Province / Territory</Label>
              <Select
                value={province || undefined}
                onValueChange={(v) => saveJurisdiction.mutate({ jurisdictionCountry: country, jurisdictionProvince: v })}
                disabled={saveJurisdiction.isPending}
              >
                <SelectTrigger data-testid="settings-select-province">
                  <SelectValue placeholder="Select province" />
                </SelectTrigger>
                <SelectContent>
                  {CA_PROVINCES.map((p) => (
                    <SelectItem key={p.code} value={p.code} data-testid={`settings-province-${p.code}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface SettingsPageProps {
  clientData: Client | null;
  /** s228 (PLAN-HOME-1): inside the plan's frame, the board's groups. */
  grouped?: boolean;
}

/** s228 — one of board 12's Settings groups (Home.dc.html:1090-1126): the name in Fraunces, its sub line under it. */
function SettingsGroup({ name, sub, id, children }: { name: string; sub: string; id: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4" data-testid={`settings-group-${id}`}>
      <div className="flex flex-col gap-0.5">
        <h2 className="font-serif text-[18px] font-normal text-warm-charcoal">{name}</h2>
        {sub && <span className="text-[13px] text-warm-muted">{sub}</span>}
      </div>
      {children}
    </section>
  );
}

export default function SettingsPage({ clientData, grouped = false }: SettingsPageProps) {
  const uid = useId();
  const { user, logout } = useAuth();
  const { toast } = useToast();

  // Deep-link from the dashboard "Manage" link: /settings#secure-file scrolls the Secure
  // Information File section into view once it's mounted.
  useEffect(() => {
    if (window.location.hash !== "#secure-file") return;
    const t = setTimeout(() => {
      document.getElementById("secure-file")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, []);

  // ── My Situation ──────────────────────────────────────────────────────────
  const [pendingFlagToggle, setPendingFlagToggle] = useState<{
    flag: keyof Client;
    newValue: boolean;
    label: string;
  } | null>(null);

  const [investmentTypes, setInvestmentTypes] = useState<string[]>([]);

  // Saved INV_TYPE selections live as generic_entries (Investments section), NOT a clients
  // column — so seed the checkboxes from the dedicated read endpoint rather than from
  // clientData. (Bug fix, s112 SF-5: the checkboxes previously hard-reset to [] on load and
  // never reflected the saved values.)
  const { data: savedInvestmentTypes } = useQuery<string[]>({
    queryKey: ["/api/clients", clientData?.id, "investment-types"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientData!.id}/investment-types`);
      if (!res.ok) return [];
      const body = await res.json();
      return Array.isArray(body?.types) ? body.types : [];
    },
    enabled: !!clientData?.id,
  });

  const updateFlagMutation = useMutation({
    mutationFn: async (payload: Record<string, boolean | string[]>) => {
      const res = await apiRequest("PATCH", `/api/clients/${clientData!.id}/situation-flags`, payload);
      if (!res.ok) throw new Error((await res.json()).message || "Failed to update");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: "Updated" });
      setPendingFlagToggle(null);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const FLAG_LABELS: Array<{ key: keyof Client; label: string; sectionName: string }> = [
    { key: "hasChildren",    label: "I have children",    sectionName: "Children" },
    { key: "hasPets",        label: "I have pets",         sectionName: "Pets" },
    { key: "ownsProperty",   label: "I own property",      sectionName: "Properties" },
    { key: "ownsBusiness",   label: "I own a business",    sectionName: "Business" },
    { key: "hasInvestments", label: "I have investments",  sectionName: "Investments" },
    { key: "isRenter",       label: "I rent my home",      sectionName: "Housing" },
  ];

  const handleFlagToggle = (flag: keyof Client, currentValue: boolean, label: string, sectionName: string) => {
    if (currentValue) {
      // Turning OFF — show confirmation
      setPendingFlagToggle({ flag, newValue: false, label: sectionName });
    } else {
      // Turning ON — no confirmation needed
      updateFlagMutation.mutate({ [flag]: true });
    }
  };

  // Seed / re-seed local checkbox state from the saved values (on load and when the client
  // or the fetched selections change). Local state stays the optimistic source of truth while
  // toggling; the mutation persists each change.
  useEffect(() => {
    setInvestmentTypes(savedInvestmentTypes ?? []);
  }, [savedInvestmentTypes, clientData?.id]);

  // ── Preferences ───────────────────────────────────────────────────────────
  const toggleConfirmBeforeSaving = useMutation({
    mutationFn: async (value: boolean) => {
      const res = await apiRequest("PATCH", `/api/clients/${clientData!.id}/confirm-before-saving`, { confirmBeforeSaving: value });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // ── Section 5: Account ────────────────────────────────────────────────────
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [passwordError, setPasswordError] = useState("");
  const [breachCount, setBreachCount] = useState<number | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const changePasswordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const res = await apiRequest("PUT", "/api/auth/password", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to change password");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Password changed" });
      setPasswordForm({ current: "", next: "", confirm: "" });
      setPasswordError("");
      setShowChangePassword(false);
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    },
    onError: (error: any) => {
      setPasswordError(error.message);
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/auth/account");
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to delete account");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast({ title: "Account deleted" });
      await logout();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleChangePassword = () => {
    setPasswordError("");
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError("Passwords don't match");
      return;
    }
    if (!validatePasswordComplexity(passwordForm.next)) {
      setPasswordError(PASSWORD_COMPLEXITY_MESSAGE);
      return;
    }
    changePasswordMutation.mutate({ currentPassword: passwordForm.current, newPassword: passwordForm.next });
  };

  if (!clientData) {
    return (
      <div className="container max-w-3xl mx-auto p-6">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // s228 (PLAN-HOME-1, Reade ruling E): inside the plan's frame, Settings takes board 12's groups — Account ·
  // Security · Notifications — plus "More plan settings" for the six choices whose own homes are not built yet.
  // Outside it (the interview flag off), the page is exactly as it was. Each section is written ONCE, below.
  const S = {
      situation: (
        <>
        {/* ── My Situation ── */}
      <Card>
        <CardHeader>
          <CardTitle>My Situation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {FLAG_LABELS.map(({ key, label, sectionName }) => (
            <div key={key}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{label}</p>
                </div>
                <Switch
                  checked={!!(clientData[key] as boolean | null)}
                  onCheckedChange={(val) => handleFlagToggle(key, !!(clientData[key] as boolean | null), label, sectionName)}
                  disabled={updateFlagMutation.isPending}
                />
              </div>
              {key === "hasInvestments" && clientData.hasInvestments && (
                <div className="mt-3 ml-4 space-y-2">
                  <p className="text-xs text-muted-foreground">Investment types:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {INVESTMENT_TYPES.map((type) => (
                      <div key={type} className="flex items-center gap-2">
                        <Checkbox
                          id={`inv-${type}`}
                          checked={investmentTypes.includes(type)}
                          onCheckedChange={(checked) => {
                            const next = checked
                              ? [...investmentTypes, type]
                              : investmentTypes.filter(t => t !== type);
                            setInvestmentTypes(next);
                            updateFlagMutation.mutate({ hasInvestments: true, investmentTypes: next });
                          }}
                        />
                        <label htmlFor={`inv-${type}`} className="text-sm">{type}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Confirmation dialog for turning OFF a flag */}
      <AlertDialog open={!!pendingFlagToggle} onOpenChange={(open) => { if (!open) setPendingFlagToggle(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hide {pendingFlagToggle?.label} sections?</AlertDialogTitle>
            <AlertDialogDescription>
              This will hide {pendingFlagToggle?.label} sections from your guided journey. Your saved data won't be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingFlagToggle) {
                  updateFlagMutation.mutate({ [pendingFlagToggle.flag]: false });
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </>
      ),
      jurisdiction: (
        <>
        {/* ── Jurisdiction (Stephanie #9) ── */}
      {clientData && <JurisdictionSection clientData={clientData} />}
        </>
      ),
      deathAndMessage: (
        <>
        {/* Death-verification settings (plan-level) + message to your executor. Executor
          naming/roles live in the expandable section under Legal → Your Executor. */}
      {clientData?.id && <DeathVerificationSettings clientId={clientData.id} />}
      {clientData && <MessageToExecutorCard clientData={clientData} />}
        </>
      ),
      secureFile: (
        <>
        {/* ── Secure Information File (s89): SDE Cat A file management. ── */}
      {clientData && <SecureFileSettings clientData={clientData} />}
        </>
      ),
      preferences: (
        <>
        {/* ── Section 4: Preferences ── */}
      <Card>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium">Review extracted fields before saving</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                When you upload a document, review the extracted information before it's saved to your plan.
              </p>
            </div>
            <Switch
              checked={clientData.confirmBeforeSaving !== false}
              key={String(clientData.confirmBeforeSaving)}
              onCheckedChange={(val) => toggleConfirmBeforeSaving.mutate(val)}
              disabled={toggleConfirmBeforeSaving.isPending}
            />
          </div>
          <div className="flex items-start justify-between gap-4 opacity-50">
            <div className="flex-1">
              <p className="text-sm font-medium">Email notifications <span className="text-xs text-muted-foreground">(Coming soon)</span></p>
            </div>
            <Switch disabled />
          </div>
          <div className="flex items-start justify-between gap-4 opacity-50">
            <div className="flex-1">
              <p className="text-sm font-medium">Annual reminder <span className="text-xs text-muted-foreground">(Coming soon)</span></p>
            </div>
            <Switch disabled />
          </div>
        </CardContent>
      </Card>
        </>
      ),
      advisor: (
        <>
        {/* ── Section 5: Your Advisor ── */}
      <AdvisorLinkSection clientId={clientData.id} />
        </>
      ),
      mfa: (
        <>
        {/* ── Security: two-factor authentication (SMS MFA, s90) ── */}
      <MfaSecuritySection defaultPhone={clientData.phone} />
        </>
      ),
      account: (
        <>
        {/* ── Section 6: Account ── */}
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs text-muted-foreground">Email</p>
            <p className="text-sm font-medium">{user?.email}</p>
          </div>

          {/* Change password */}
          {!showChangePassword ? (
            <Button variant="outline" size="sm" onClick={() => setShowChangePassword(true)}>
              Change password
            </Button>
          ) : (
            <div className="space-y-3 p-4 border rounded-lg">
              <p className="text-sm font-medium">Change password</p>
              <div className="space-y-2">
                <Label className="text-xs" htmlFor={`${uid}-current-password`}>Current password</Label>
                <Input id={`${uid}-current-password`}
                  type="password"
                  value={passwordForm.current}
                  onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs" htmlFor={`${uid}-new-password`}>New password</Label>
                <Input id={`${uid}-new-password`}
                  type="password"
                  value={passwordForm.next}
                  onChange={e => {
                    setPasswordForm(p => ({ ...p, next: e.target.value }));
                    setBreachCount(null);
                  }}
                  onBlur={async (e) => {
                    const pwd = e.target.value;
                    if (pwd.length >= 10) {
                      const count = await checkBreachedPassword(pwd);
                      setBreachCount(count);
                    }
                  }}
                  placeholder="At least 10 characters"
                />
                <PasswordStrengthMeter password={passwordForm.next} />
                {breachCount !== null && breachCount > 0 && (
                  <p className="text-xs mt-1" style={{ color: "#C53030" }}>
                    <AlertTriangle className="h-3.5 w-3.5 inline-block shrink-0 align-[-2px]" aria-hidden="true" /> This password has appeared in {breachCount.toLocaleString()} data breach{breachCount === 1 ? "" : "es"}. Choose a different password.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs" htmlFor={`${uid}-confirm-password`}>Confirm new password</Label>
                <Input id={`${uid}-confirm-password`}
                  type="password"
                  value={passwordForm.confirm}
                  onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                />
              </div>
              {passwordError && <p className="text-xs text-destructive">{passwordError}</p>}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleChangePassword}
                  disabled={changePasswordMutation.isPending}
                >
                  {changePasswordMutation.isPending ? "Saving..." : "Update"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ current: "", next: "", confirm: "" });
                  setPasswordError("");
                }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Delete account */}
          <div className="pt-4 border-t">
            <button
              className="text-sm text-destructive hover:underline"
              onClick={() => setShowDeleteDialog(true)}
            >
              Delete my account
            </button>
          </div>
        </CardContent>
      </Card>
        </>
      ),
      dialog: (
        <>
        {/* Delete account confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your estate plan and all associated data. This action cannot be undone.
              Type <strong>DELETE</strong> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            className="mt-2"
            value={deleteConfirmText}
            onChange={e => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
          />
          <AlertDialogFooter className="mt-2">
            <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleteConfirmText !== "DELETE" || deleteAccountMutation.isPending}
              onClick={() => deleteAccountMutation.mutate()}
            >
              {deleteAccountMutation.isPending ? "Deleting..." : "Delete account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </>
      ),
  };
  const G = HOME_BOARD.settings;
  return (
    <div className="container max-w-3xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold">Settings</h1>
      {grouped ? (
        <>
          <SettingsGroup name={G[0].name} sub={G[0].sub} id="account">{S.account}</SettingsGroup>
          <SettingsGroup name={G[1].name} sub={G[1].sub} id="security">{S.mfa}</SettingsGroup>
          <SettingsGroup name={G[2].name} sub={G[2].sub} id="notifications">{S.preferences}</SettingsGroup>
          <SettingsGroup name={G[3].name} sub={G[3].sub} id="more">
            {S.situation}{S.jurisdiction}{S.deathAndMessage}{S.secureFile}{S.advisor}
          </SettingsGroup>
        </>
      ) : (
        <>{S.situation}{S.jurisdiction}{S.deathAndMessage}{S.secureFile}{S.preferences}{S.advisor}{S.mfa}{S.account}</>
      )}
      {S.dialog}
    </div>
  );
}
